import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type { RootState } from '../../../store/store';
import { fetchImplementationSummary, syncImplementationProgress } from './isoImplementationApi';
import type { ImplementationSummary } from './types';

export const loadImplementationSummary = createAsyncThunk(
  'isoImplementation/loadSummary',
  async (standard: string = 'iso9001') => fetchImplementationSummary(standard),
);

export const syncImplementationProgressThunk = createAsyncThunk(
  'isoImplementation/syncProgress',
  async (standard: string = 'iso9001') => syncImplementationProgress(standard),
);

interface IsoImplementationState {
  summaries: Record<string, ImplementationSummary | undefined>;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  syncStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
  syncError: string | null;
}

const initialState: IsoImplementationState = {
  summaries: {},
  status: 'idle',
  syncStatus: 'idle',
  error: null,
  syncError: null,
};

const isoImplementationSlice = createSlice({
  name: 'isoImplementation',
  initialState,
  reducers: {
    invalidateImplementationSummary(state, action: { payload: string }) {
      delete state.summaries[action.payload];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadImplementationSummary.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(loadImplementationSummary.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.summaries[action.meta.arg ?? 'iso9001'] = action.payload;
      })
      .addCase(loadImplementationSummary.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message ?? 'Error al cargar avance';
      })
      .addCase(syncImplementationProgressThunk.pending, (state) => {
        state.syncStatus = 'loading';
        state.syncError = null;
      })
      .addCase(syncImplementationProgressThunk.fulfilled, (state, action) => {
        state.syncStatus = 'succeeded';
        state.summaries[action.meta.arg ?? 'iso9001'] = action.payload.summary;
        state.status = 'succeeded';
      })
      .addCase(syncImplementationProgressThunk.rejected, (state, action) => {
        state.syncStatus = 'failed';
        state.syncError = action.error.message ?? 'Error al sincronizar evidencia';
      });
  },
});

export const { invalidateImplementationSummary } = isoImplementationSlice.actions;

export const selectImplementationSummary = (standard: string) => (state: RootState) =>
  state.isoImplementation.summaries[standard];

export const selectImplementationSummaryStatus = (state: RootState) =>
  state.isoImplementation.status;

export const selectImplementationSyncStatus = (state: RootState) =>
  state.isoImplementation.syncStatus;

export default isoImplementationSlice.reducer;
